/**
 * Displays a chronological progression of parameter readings.
 *
 * - 0 values: renders nothing
 * - 1–2 values: inline with arrow separators (val₁ → val₂)
 * - 3+ values: shows the latest value with a "(N readings)" hover card listing all
 */
import {createStyles, Group, HoverCard, Stack, Text, UnstyledButton} from '@mantine/core';
import React from 'react';

const ARROW_SEPARATOR = ' → ';
const MAX_ARROW_VALUES = 2;

const useStyles = createStyles(() => ({
  readingsTrigger: {
    cursor: 'pointer',
  },
}));

/**
 * Latest reading plus a hover card listing every reading. HoverCard manages the open/close
 * lifecycle, including the grace period that keeps the card open while the pointer travels from
 * the trigger onto the dropdown.
 */
function CollapsedReadings({
  latestValue,
  readingsList,
  count,
}: {
  latestValue: React.ReactNode;
  readingsList: Array<React.ReactNode>;
  count: number;
}): JSX.Element {
  const {classes} = useStyles();

  return (
    <Group spacing="sm" align="center">
      <Text fz="xs" c="gray.9" component="div">
        {latestValue}
      </Text>
      <HoverCard
        width={200}
        shadow="md"
        withArrow={true}
        position="top"
        withinPortal={true}
        openDelay={300}
        closeDelay={100}
      >
        <HoverCard.Target>
          <UnstyledButton type="button" className={classes.readingsTrigger}>
            <Text span={true} fz="xs" c="blue.6">
              ({count} readings)
            </Text>
          </UnstyledButton>
        </HoverCard.Target>
        <HoverCard.Dropdown>
          <Stack spacing={2}>
            {readingsList.map((val, i) => (
              <Text key={i} fz="xs">
                {i + 1}. {val}
              </Text>
            ))}
          </Stack>
        </HoverCard.Dropdown>
      </HoverCard>
    </Group>
  );
}

export default function MultiValueDisplay({
  values,
  tooltipValues,
}: {
  values: Array<React.ReactNode>;
  /** Optional alternate representation for the hover card's readings list (defaults to `values`). */
  tooltipValues?: Array<React.ReactNode>;
}): JSX.Element | null {
  if (values.length === 0) {
    return null;
  }

  if (values.length <= MAX_ARROW_VALUES) {
    return (
      <Text fz="xs" c="gray.9" component="div">
        {values.map((val, i) => (
          <React.Fragment key={i}>
            {i > 0 && ARROW_SEPARATOR}
            {val}
          </React.Fragment>
        ))}
      </Text>
    );
  }

  return (
    <CollapsedReadings
      latestValue={values[values.length - 1]}
      readingsList={tooltipValues ?? values}
      count={values.length}
    />
  );
}
